import type { Language } from '@lifetimeline/shared';

import { useI18n } from '../i18n/index.js';
import { Layout } from '../components/Layout.js';
import { CONTACT_EMAIL } from '../lib/branding.js';
import styles from './pageStyles.module.css';

/**
 * 개인정보처리방침.
 *
 * **구글이 요구해서 만든 페이지다.** OAuth 앱을 테스트 모드에서 프로덕션으로
 * 올리려면 이 주소가 있어야 한다(앱 이름·지원 이메일·홈페이지와 함께). 심사를
 * 받는 것과는 별개다 — 기본 스코프만 쓰면 심사는 없고, 이 링크만 있으면 된다.
 *
 * 내용은 **실제로 저장하는 것만** 적는다. 다른 데서 베껴 온 문구를 붙이면
 * 안 받는 정보를 받는다고 적어 두게 되고, 그게 더 나쁜 거짓말이다.
 * 저장하는 것이 바뀌면 여기도 바꾼다.
 *
 * 문구는 i18n에 두지 않고 여기 둔다 — 한 화면에서만 쓰는 긴 글이라
 * 키로 쪼개 두면 읽을 때 문장이 이어지지 않는다.
 */

interface Section {
  title: string;
  body: readonly string[];
}

const SECTIONS: Record<Language, readonly Section[]> = {
  ko: [
    {
      title: '무엇을 받나요',
      body: [
        '구글 로그인을 하면 구글이 계정 고유 번호, 이메일 주소, 이름, 프로필 사진을 줍니다. 비밀번호는 받지 않습니다.',
        '그 외에는 직접 적어 넣은 것만 있습니다 — 프로필(주소·이름·한마디), 타임라인의 주제와 기록, 취향으로 고른 것, 올린 사진.',
      ],
    },
    {
      title: '누가 볼 수 있나요',
      body: [
        '프로필 주소(life-timeline.com/@내주소)로 들어오면 누구나 볼 수 있습니다. 계정을 잠그면 인연을 맺은 사람만 봅니다.',
        '이메일 주소는 화면 어디에도 보이지 않습니다.',
      ],
    },
    {
      title: '조회 기록',
      body: [
        '로그인한 사람이 남의 프로필을 열면 "누가 봤는지"가 기록됩니다. 이 기록은 90일이 지나면 자동으로 지워집니다.',
        '비로그인으로 본 것은 누구인지 알 수 없어 기록하지 않습니다.',
      ],
    },
    {
      title: '어디에 두나요',
      body: [
        '아마존 웹 서비스(AWS) 서울 리전에 저장합니다. 그 외에 다른 곳으로 넘기거나 팔지 않습니다.',
        '광고를 붙이지 않고, 광고 목적으로 제3자에게 제공하지 않습니다.',
      ],
    },
    {
      title: '지우고 싶을 때',
      body: [
        '기록과 취향은 화면에서 직접 지울 수 있습니다.',
        '계정을 통째로 지우고 싶으면 아래 주소로 알려주세요. 확인한 뒤 계정과 올린 것을 모두 지웁니다.',
      ],
    },
  ],
  en: [
    {
      title: 'What we receive',
      body: [
        'When you sign in with Google, Google gives us your account identifier, email address, name, and profile picture. We never receive your password.',
        'Everything else is what you type in — your profile (handle, name, bio), timeline topics and records, the things you pick, and images you upload.',
      ],
    },
    {
      title: 'Who can see it',
      body: [
        'Anyone who opens your profile address (life-timeline.com/@yourhandle) can see it. If you lock your account, only people you are connected with can.',
        'Your email address is never shown anywhere on the site.',
      ],
    },
    {
      title: 'Profile views',
      body: [
        "When a signed-in person opens someone's profile, that view is recorded. These records are deleted automatically after 90 days.",
        'Views by signed-out visitors are not recorded, because there is no one to name.',
      ],
    },
    {
      title: 'Where it is kept',
      body: [
        'On Amazon Web Services in the Seoul region. We do not hand it to anyone else or sell it.',
        'There are no ads, and nothing is shared with third parties for advertising.',
      ],
    },
    {
      title: 'Deleting your data',
      body: [
        'You can delete records and picks yourself from the screen.',
        'To delete your whole account, email the address below. We will confirm and remove the account and everything you uploaded.',
      ],
    },
  ],
  ja: [
    {
      title: '受け取る情報',
      body: [
        'Googleでログインすると、Googleからアカウント識別子、メールアドレス、名前、プロフィール写真を受け取ります。パスワードを受け取ることはありません。',
        'それ以外はすべて、あなたが入力したものです。プロフィール（ID、名前、ひとこと）、タイムラインのトピックと記録、選んだもの、アップロードした画像です。',
      ],
    },
    {
      title: '誰が見られるか',
      body: [
        'あなたのプロフィールのアドレス（life-timeline.com/@あなたのID）を開いた人は誰でも見られます。アカウントを非公開にすると、つながっている人だけが見られます。',
        'メールアドレスがサイト上に表示されることはありません。',
      ],
    },
    {
      title: 'プロフィールの閲覧',
      body: [
        'ログインしている人が誰かのプロフィールを開くと、その閲覧が記録されます。この記録は90日後に自動で削除されます。',
        'ログインしていない訪問者の閲覧は記録されません。誰が見たのかを示しようがないためです。',
      ],
    },
    {
      title: '保存場所',
      body: [
        'Amazon Web Servicesのソウルリージョンに保存しています。他者に提供したり、販売したりすることはありません。',
        '広告はなく、広告目的で第三者と共有することもありません。',
      ],
    },
    {
      title: 'データの削除',
      body: [
        '記録と選んだものは、画面から自分で削除できます。',
        'アカウントごと削除したい場合は、下記のアドレスにメールしてください。確認のうえ、アカウントとアップロードしたものをすべて削除します。',
      ],
    },
  ],
  'zh-cn': [
    {
      title: '我们获取哪些信息',
      body: [
        '使用 Google 登录时，Google 会向我们提供你的账号标识、邮箱地址、姓名和头像。我们不会获取你的密码。',
        '其余信息都是你自己填写的内容——个人资料（主页地址、名称、简介）、时间线中的主题和记录、你选的东西，以及你上传的图片。',
      ],
    },
    {
      title: '谁能看到',
      body: [
        '任何人打开你的主页地址（life-timeline.com/@你的地址）都能看到。如果你将账号设为私密，则只有你人脉中的人能看到。',
        '你的邮箱地址不会在网站的任何地方显示。',
      ],
    },
    {
      title: '主页浏览记录',
      body: [
        '已登录的用户打开他人主页时，这次浏览会被记录。这些记录会在 90 天后自动删除。',
        '未登录访客的浏览不会被记录，因为无法知道是谁。',
      ],
    },
    {
      title: '数据存放在哪里',
      body: [
        '存放在亚马逊云科技（Amazon Web Services）首尔区域。我们不会将其提供给任何其他人，也不会出售。',
        '网站没有广告，也不会为投放广告而与第三方共享任何信息。',
      ],
    },
    {
      title: '删除你的数据',
      body: [
        '你可以在页面上自行删除记录和所选内容。',
        '如需删除整个账号，请发邮件至下方地址。我们确认后，会删除该账号及你上传的所有内容。',
      ],
    },
  ],
  'zh-tw': [
    {
      title: '我們會取得哪些資料',
      body: [
        '使用 Google 登入時，Google 會提供你的帳戶識別碼、電子郵件地址、姓名和大頭貼。我們絕不會取得你的密碼。',
        '其他資料都是你自己填寫的內容：個人檔案（個人網址、名稱、自我介紹）、時間軸的主題與紀錄、你選擇的項目，以及你上傳的圖片。',
      ],
    },
    {
      title: '誰可以看到',
      body: [
        '任何人只要開啟你的個人檔案網址（life-timeline.com/@你的帳號），就能看到你的內容。鎖定帳號後，只有你人脈中的人才看得到。',
        '你的電子郵件地址不會顯示在網站上的任何地方。',
      ],
    },
    {
      title: '個人檔案瀏覽紀錄',
      body: [
        '已登入的使用者開啟他人的個人檔案時，這次瀏覽會被記錄下來。這些紀錄會在 90 天後自動刪除。',
        '未登入的訪客瀏覽不會被記錄，因為無法得知對方是誰。',
      ],
    },
    {
      title: '資料存放在哪裡',
      body: [
        '存放在 Amazon Web Services（AWS）的首爾區域。我們不會把資料交給其他任何人，也不會出售。',
        '網站沒有廣告，也不會為了廣告目的將任何資料提供給第三方。',
      ],
    },
    {
      title: '刪除你的資料',
      body: [
        '你可以直接在畫面上刪除紀錄和選擇的項目。',
        '如果想刪除整個帳號，請寄信到下方的電子郵件地址。我們確認後，會刪除你的帳號以及你上傳的所有內容。',
      ],
    },
  ],
  es: [
    {
      title: 'Qué recibimos',
      body: [
        'Cuando inicias sesión con Google, Google nos da el identificador de tu cuenta, tu correo electrónico, tu nombre y tu foto de perfil. Nunca recibimos tu contraseña.',
        'Todo lo demás es lo que tú escribes: tu perfil (usuario, nombre y biografía), los temas y registros de tu cronología, lo que eliges y las imágenes que subes.',
      ],
    },
    {
      title: 'Quién puede verlo',
      body: [
        'Cualquiera que abra la dirección de tu perfil (life-timeline.com/@tuusuario) puede verlo. Si bloqueas tu cuenta, solo pueden verlo las personas con las que tienes conexión.',
        'Tu correo electrónico nunca se muestra en ninguna parte del sitio.',
      ],
    },
    {
      title: 'Visitas al perfil',
      body: [
        'Cuando una persona con sesión iniciada abre el perfil de alguien, esa visita se registra. Estos registros se eliminan automáticamente a los 90 días.',
        'Las visitas de quienes no han iniciado sesión no se registran, porque no hay nadie a quien nombrar.',
      ],
    },
    {
      title: 'Dónde se guarda',
      body: [
        'En Amazon Web Services, en la región de Seúl. No se lo entregamos a nadie más ni lo vendemos.',
        'No hay anuncios y no se comparte nada con terceros con fines publicitarios.',
      ],
    },
    {
      title: 'Eliminar tus datos',
      body: [
        'Puedes eliminar tú mismo los registros y los favoritos desde la pantalla.',
        'Para eliminar toda tu cuenta, escribe a la dirección de correo que aparece abajo. Lo confirmaremos y eliminaremos la cuenta y todo lo que hayas subido.',
      ],
    },
  ],
  pt: [
    {
      title: 'O que recebemos',
      body: [
        'Quando você entra com o Google, o Google nos envia o identificador da sua conta, seu endereço de e-mail, seu nome e sua foto de perfil. Nunca recebemos sua senha.',
        'Todo o resto é o que você digita — seu perfil (usuário, nome, bio), os temas e registros da sua linha do tempo, as coisas que você escolhe e as imagens que você envia.',
      ],
    },
    {
      title: 'Quem pode ver',
      body: [
        'Qualquer pessoa que abrir o endereço do seu perfil (life-timeline.com/@seuusuario) pode ver. Se você trancar sua conta, só as pessoas conectadas a você podem ver.',
        'Seu endereço de e-mail nunca aparece em nenhum lugar do site.',
      ],
    },
    {
      title: 'Visualizações do perfil',
      body: [
        'Quando uma pessoa com login abre o perfil de alguém, essa visualização é registrada. Esses registros são apagados automaticamente após 90 dias.',
        'Visitas de quem não está logado não são registradas, porque não há ninguém para identificar.',
      ],
    },
    {
      title: 'Onde fica guardado',
      body: [
        'Na Amazon Web Services, na região de Seul. Não repassamos nem vendemos esses dados a ninguém.',
        'Não há anúncios, e nada é compartilhado com terceiros para fins de publicidade.',
      ],
    },
    {
      title: 'Como apagar seus dados',
      body: [
        'Você pode apagar registros e escolhas direto pela tela.',
        'Para apagar sua conta inteira, envie um e-mail para o endereço abaixo. Vamos confirmar e remover a conta e tudo o que você enviou.',
      ],
    },
  ],
};

export function PrivacyPage() {
  const { t, language } = useI18n();
  const sections = SECTIONS[language];

  return (
    <Layout narrow>
      <div className={styles.head}>
        <h1 className={styles.title}>{t('legal.privacyTitle')}</h1>
      </div>

      {sections.map((section) => (
        <section key={section.title} className={styles.section}>
          <h2 className={styles.legalHeading}>{section.title}</h2>
          {section.body.map((line) => (
            <p key={line} className={styles.legalText}>
              {line}
            </p>
          ))}
        </section>
      ))}

      <section className={styles.section}>
        <h2 className={styles.legalHeading}>{t('common.email')}</h2>
        <p className={styles.legalText}>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </section>
    </Layout>
  );
}
